"use client";
import { ThemedSelect } from "./ThemedSelect";


import React, { useCallback, useId, useMemo, useState } from "react";

import {
  emptyGuidedRequestQuoteAnswers,
  formatGuidedRequestQuoteMessage,
  guidedRequestQuoteBudgetOptions,
  guidedRequestQuoteServiceOptions,
  guidedRequestQuoteTimingOptions,
  isGuidedRequestQuoteValid,
  sanitizeGuidedRequestQuoteAnswers,
  validateGuidedRequestQuoteAnswers,
  type GuidedRequestQuoteAnswers,
  type GuidedRequestQuoteLabels,
} from "./guidedRequestQuote";

export type GuidedRequestQuoteComposerSubmitResult = Readonly<{
  threadId?: string;
}>;

export type GuidedRequestQuoteSmsConsentLabels = Readonly<{
  phoneLabel: string;
  phonePlaceholder: string;
  privacy: string;
  disclosure: string;
  consentLabel: string;
  consentRequired: string;
  wordingVersion: string;
  legalSlot?: React.ReactNode;
}>;

export type GuidedRequestQuoteProjectLocationFieldProps = Readonly<{
  id: string;
  value: string;
  ariaDescribedBy?: string;
  ariaInvalid: boolean;
  onChange: (value: string) => void;
  placeholder: string;
}>;

export type GuidedRequestQuoteComposerProps = Readonly<{
  labels: GuidedRequestQuoteLabels;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (input: Readonly<{
    message: string;
    viewerPhone?: string;
    serviceSmsConsent?: boolean;
  }>) => Promise<GuidedRequestQuoteComposerSubmitResult>;
  onOpenConversation?: (threadId: string) => void;
  /** Optional anonymous email/captcha slot rendered above actions on the form step. */
  identitySlot?: React.ReactNode;
  className?: string;
  instanceName?: string;
  /** Optional governed location field adapter, for example a backend catalog combobox. */
  renderProjectLocationField?: (
    props: GuidedRequestQuoteProjectLocationFieldProps,
  ) => React.ReactNode;
  /** Optional extra validation layered on top of the shared required-field contract. */
  validateProjectLocation?: (value: string) => string | undefined;
  smsConsent?: GuidedRequestQuoteSmsConsentLabels;
}>;

type ComposerStep = "form" | "review" | "success";

function classifySubmitError(
  error: unknown,
  labels: GuidedRequestQuoteLabels,
): string {
  const text = error instanceof Error ? error.message : String(error ?? "");
  const lower = text.toLowerCase();
  if (lower.includes("rate") || lower.includes("limited") || lower.includes("temporarily")) {
    return labels.rateLimited;
  }
  if (lower.includes("already submitted") || lower.includes("duplicate") || lower.includes("conflict")) {
    return labels.duplicate;
  }
  return labels.submitFailed;
}

export function GuidedRequestQuoteComposer({
  labels,
  isSubmitting,
  onCancel,
  onSubmit,
  onOpenConversation,
  identitySlot,
  className,
  instanceName = "guidedRequestQuoteComposer",
  renderProjectLocationField,
  validateProjectLocation,
  smsConsent,
}: GuidedRequestQuoteComposerProps) {
  const titleId = useId();
  const fieldId = useId();
  const [answers, setAnswers] = useState<GuidedRequestQuoteAnswers>(emptyGuidedRequestQuoteAnswers);
  const [step, setStep] = useState<ComposerStep>("form");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [viewerPhone, setViewerPhone] = useState("");
  const [serviceSmsConsent, setServiceSmsConsent] = useState(false);

  const previewMessage = useMemo(
    () => formatGuidedRequestQuoteMessage(answers, labels),
    [answers, labels],
  );

  const updateField = useCallback(
    <K extends keyof GuidedRequestQuoteAnswers>(key: K, value: GuidedRequestQuoteAnswers[K]) => {
      setAnswers((current) => ({ ...current, [key]: value }));
      setFieldErrors((current) => {
        if (!(key in current) && !("message" in current)) return current;
        const next = { ...current };
        delete next[key];
        delete next.message;
        return next;
      });
    },
    [],
  );

  const validateAnswers = useCallback((candidate: GuidedRequestQuoteAnswers) => {
    const errors: Record<string, string> = {
      ...validateGuidedRequestQuoteAnswers(candidate, labels),
    };
    const locationError = validateProjectLocation?.(
      sanitizeGuidedRequestQuoteAnswers(candidate).projectLocation,
    );
    if (locationError) {
      errors.projectLocation = locationError;
    }
    if (smsConsent && viewerPhone.trim() && !serviceSmsConsent) {
      errors.viewerPhone = smsConsent.consentRequired;
    }
    return errors;
  }, [labels, serviceSmsConsent, smsConsent, validateProjectLocation, viewerPhone]);

  const goToReview = useCallback(() => {
    const errors = validateAnswers(answers);
    setFieldErrors(errors);
    setSubmitError(null);
    if (Object.keys(errors).length > 0) return;
    setAnswers(sanitizeGuidedRequestQuoteAnswers(answers));
    setStep("review");
  }, [answers, validateAnswers]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    const errors = validateAnswers(answers);
    if (!isGuidedRequestQuoteValid(answers, labels) || Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setStep("form");
      return;
    }
    setSubmitError(null);
    const message = formatGuidedRequestQuoteMessage(answers, labels);
    const phone = smsConsent ? viewerPhone.trim() : "";
    try {
      const result = await onSubmit({
        message,
        ...(phone ? { viewerPhone: phone, serviceSmsConsent } : {}),
      });
      setThreadId(result.threadId);
      setStep("success");
    } catch (error) {
      setSubmitError(classifySubmitError(error, labels));
    }
  }, [
    answers,
    isSubmitting,
    labels,
    onSubmit,
    serviceSmsConsent,
    smsConsent,
    validateAnswers,
    viewerPhone,
  ]);

  return (
    <section
      className={["guided-request-quote", className].filter(Boolean).join(" ")}
      data-component-instance={instanceName}
      aria-labelledby={titleId}
    >
      {step === "success" ? (
        <div className="guided-request-quote__success" role="status">
          <h2 id={titleId}>{labels.successTitle}</h2>
          <p>{labels.successBody}</p>
          <div className="guided-request-quote__actions">
            {threadId && onOpenConversation ? (
              <button
                type="button"
                className="guided-request-quote__primary"
                onClick={() => onOpenConversation(threadId)}
              >
                {labels.openConversation}
              </button>
            ) : null}
            <button type="button" onClick={onCancel}>
              {labels.close}
            </button>
          </div>
        </div>
      ) : null}

      {step === "review" ? (
        <>
          <header className="guided-request-quote__header">
            <h2 id={titleId}>{labels.reviewHeading}</h2>
          </header>
          <label className="guided-request-quote__field">
            <span>{labels.messagePreviewLabel}</span>
            <textarea
              readOnly
              rows={10}
              value={previewMessage}
              className="guided-request-quote__preview"
            />
          </label>
          {smsConsent && viewerPhone.trim() ? (
            <fieldset className="guided-request-quote__sms guided-request-quote__sms--review">
              <legend>{smsConsent.phoneLabel}</legend>
              <p className="guided-request-quote__review-phone">{viewerPhone.trim()}</p>
              <p className="guided-request-quote__privacy">{smsConsent.privacy}</p>
              <p className="guided-request-quote__disclosure">{smsConsent.disclosure}</p>
              <p className="guided-request-quote__consent">
                <span aria-hidden="true">✓</span>
                <span>{smsConsent.consentLabel}</span>
              </p>
              {smsConsent.legalSlot}
              <p className="guided-request-quote__wording">{smsConsent.wordingVersion}</p>
            </fieldset>
          ) : null}
          {submitError ? (
            <p className="guided-request-quote__error" role="alert">{submitError}</p>
          ) : null}
          <footer className="guided-request-quote__actions">
            <button type="button" onClick={() => setStep("form")} disabled={isSubmitting}>
              {labels.backToForm}
            </button>
            <button
              type="button"
              className="guided-request-quote__primary"
              onClick={() => { void handleSubmit(); }}
              disabled={isSubmitting}
            >
              {isSubmitting ? labels.submitting : labels.submit}
            </button>
          </footer>
        </>
      ) : null}

      {step === "form" ? (
        <>
          <header className="guided-request-quote__header">
            <h2 id={titleId}>{labels.heading}</h2>
          </header>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-service`}>{labels.serviceNeededLabel}</label>
            <ThemedSelect
              id={`${fieldId}-service`}
              value={answers.serviceNeeded}
              aria-invalid={Boolean(fieldErrors.serviceNeeded)}
              aria-describedby={fieldErrors.serviceNeeded ? `${fieldId}-service-error` : undefined}
              onChange={(event) => updateField(
                "serviceNeeded",
                event as GuidedRequestQuoteAnswers["serviceNeeded"],
              )}
            >
              <option value="">{labels.serviceNeededPlaceholder}</option>
              {guidedRequestQuoteServiceOptions.map((option) => (
                <option key={option} value={option}>
                  {labels.serviceOptions[option]}
                </option>
              ))}
            </ThemedSelect>
            {fieldErrors.serviceNeeded ? (
              <span id={`${fieldId}-service-error`} className="guided-request-quote__field-error">
                {fieldErrors.serviceNeeded}
              </span>
            ) : null}
          </div>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-location`}>{labels.projectLocationLabel}</label>
            {renderProjectLocationField ? renderProjectLocationField({
              id: `${fieldId}-location`,
              value: answers.projectLocation,
              ariaInvalid: Boolean(fieldErrors.projectLocation),
              ariaDescribedBy: fieldErrors.projectLocation
                ? `${fieldId}-location-error`
                : undefined,
              onChange: (value) => updateField("projectLocation", value),
              placeholder: labels.projectLocationPlaceholder,
            }) : (
              <input
                id={`${fieldId}-location`}
                type="text"
                value={answers.projectLocation}
                aria-invalid={Boolean(fieldErrors.projectLocation)}
                aria-describedby={fieldErrors.projectLocation ? `${fieldId}-location-error` : undefined}
                onChange={(event) => updateField("projectLocation", event.target.value)}
                placeholder={labels.projectLocationPlaceholder}
                maxLength={200}
                autoComplete="off"
              />
            )}
            {fieldErrors.projectLocation ? (
              <span id={`${fieldId}-location-error`} className="guided-request-quote__field-error">
                {fieldErrors.projectLocation}
              </span>
            ) : null}
          </div>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-timing`}>{labels.preferredTimingLabel}</label>
            <ThemedSelect
              id={`${fieldId}-timing`}
              value={answers.preferredTiming}
              aria-invalid={Boolean(fieldErrors.preferredTiming)}
              aria-describedby={fieldErrors.preferredTiming ? `${fieldId}-timing-error` : undefined}
              onChange={(event) => updateField(
                "preferredTiming",
                event as GuidedRequestQuoteAnswers["preferredTiming"],
              )}
            >
              <option value="">{labels.preferredTimingPlaceholder}</option>
              {guidedRequestQuoteTimingOptions.map((option) => (
                <option key={option} value={option}>
                  {labels.timingOptions[option]}
                </option>
              ))}
            </ThemedSelect>
            {fieldErrors.preferredTiming ? (
              <span id={`${fieldId}-timing-error`} className="guided-request-quote__field-error">
                {fieldErrors.preferredTiming}
              </span>
            ) : null}
          </div>

          <label className="guided-request-quote__field">
            <span>{labels.timingNotesLabel}</span>
            <input
              type="text"
              value={answers.timingNotes}
              onChange={(event) => updateField("timingNotes", event.target.value)}
              placeholder={labels.timingNotesPlaceholder}
              maxLength={200}
              autoComplete="off"
            />
          </label>

          <div className="guided-request-quote__field">
            <label htmlFor={`${fieldId}-scope`}>{labels.scopeSummaryLabel}</label>
            <textarea
              id={`${fieldId}-scope`}
              rows={4}
              value={answers.scopeSummary}
              aria-invalid={Boolean(fieldErrors.scopeSummary)}
              aria-describedby={fieldErrors.scopeSummary ? `${fieldId}-scope-error` : undefined}
              onChange={(event) => updateField("scopeSummary", event.target.value)}
              placeholder={labels.scopeSummaryPlaceholder}
              maxLength={1200}
            />
            {fieldErrors.scopeSummary ? (
              <span id={`${fieldId}-scope-error`} className="guided-request-quote__field-error">
                {fieldErrors.scopeSummary}
              </span>
            ) : null}
          </div>

          <label className="guided-request-quote__field">
            <span>{labels.budgetRangeLabel}</span>
            <ThemedSelect
              value={answers.budgetRange}
              onChange={(event) => updateField(
                "budgetRange",
                event as GuidedRequestQuoteAnswers["budgetRange"],
              )}
            >
              <option value="">{labels.budgetRangePlaceholder}</option>
              {guidedRequestQuoteBudgetOptions.map((option) => (
                <option key={option} value={option}>
                  {labels.budgetOptions[option]}
                </option>
              ))}
            </ThemedSelect>
          </label>

          <label className="guided-request-quote__field">
            <span>{labels.additionalNotesLabel}</span>
            <textarea
              rows={3}
              value={answers.additionalNotes}
              onChange={(event) => updateField("additionalNotes", event.target.value)}
              placeholder={labels.additionalNotesPlaceholder}
              maxLength={600}
            />
          </label>

          {smsConsent ? (
            <fieldset className="guided-request-quote__sms">
              <div className="guided-request-quote__field">
                <label htmlFor={`${fieldId}-phone`}>{smsConsent.phoneLabel}</label>
                <input
                  id={`${fieldId}-phone`}
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={viewerPhone}
                  aria-invalid={Boolean(fieldErrors.viewerPhone)}
                  aria-describedby={fieldErrors.viewerPhone ? `${fieldId}-phone-error` : undefined}
                  onChange={(event) => {
                    setViewerPhone(event.target.value);
                    setFieldErrors((current) => {
                      if (!("viewerPhone" in current)) return current;
                      const next = { ...current };
                      delete next.viewerPhone;
                      return next;
                    });
                  }}
                  placeholder={smsConsent.phonePlaceholder}
                  maxLength={20}
                />
                {fieldErrors.viewerPhone ? (
                  <span id={`${fieldId}-phone-error`} className="guided-request-quote__field-error">
                    {fieldErrors.viewerPhone}
                  </span>
                ) : null}
              </div>
              <p className="guided-request-quote__privacy">{smsConsent.privacy}</p>
              <p className="guided-request-quote__disclosure">{smsConsent.disclosure}</p>
              <label className="guided-request-quote__consent">
                <input
                  type="checkbox"
                  checked={serviceSmsConsent}
                  onChange={(event) => {
                    setServiceSmsConsent(event.target.checked);
                    setFieldErrors((current) => {
                      if (!("viewerPhone" in current)) return current;
                      const next = { ...current };
                      delete next.viewerPhone;
                      return next;
                    });
                  }}
                />
                <span>{smsConsent.consentLabel}</span>
              </label>
              {smsConsent.legalSlot}
              <p className="guided-request-quote__wording">{smsConsent.wordingVersion}</p>
            </fieldset>
          ) : null}

          {identitySlot}

          {fieldErrors.message ? (
            <p className="guided-request-quote__error" role="alert">{fieldErrors.message}</p>
          ) : null}

          <footer className="guided-request-quote__actions">
            <button type="button" onClick={onCancel} disabled={isSubmitting}>
              {labels.cancel}
            </button>
            <button
              type="button"
              className="guided-request-quote__primary"
              onClick={goToReview}
              disabled={isSubmitting}
            >
              {labels.reviewMessage}
            </button>
          </footer>
        </>
      ) : null}
    </section>
  );
}
